import React from "react";
import { Box, Typography, CardContent } from "@mui/material";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  Legend,
} from "recharts";
import { StyledCard } from "../../common/StyledCard";
import { getEntityMeta } from "../../../helpers/dashboardHelpers";
import { InventoryLogStatsDto } from "../../../shared/dtos/inventory/InventoryLogStatsDto";

const COLORS = {
  inflows: "#00C49F",
  outflows: "#FF8042",
  adjustments: "#8884d8",
};

interface InventoryLogWidgetProps {
  data: InventoryLogStatsDto;
}

const InventoryLogWidget: React.FC<InventoryLogWidgetProps> = ({ data }) => {
  const meta = getEntityMeta("INVENTORY_LOG");
  const Icon = meta.icon;

  const chartData = [
    { name: "Inflows", value: data.stockInflowsToday },
    { name: "Outflows", value: data.stockOutflowsToday },
    { name: "Adjustments", value: data.adjustmentsToday },
  ].filter((item) => item.value > 0);

  return (
    <StyledCard
      sx={{ height: "100%", display: "flex", flexDirection: "column" }}
    >
      <CardContent
        sx={{ flexGrow: 1, display: "flex", flexDirection: "column" }}
      >
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <Box
            sx={{
              p: 1,
              borderRadius: 1,
              bgcolor: `${meta.color}15`,
              color: meta.color,
              display: "flex",
              mr: 2,
            }}
          >
            <Icon />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: "bold" }}>
              {meta.title}
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Total Movements:{" "}
              <Box component="b" sx={{ fontWeight: "bold" }}>
                {data.totalMovementsToday}
              </Box>
            </Typography>
          </Box>
        </Box>

        {chartData.length > 0 ? (
          <Box sx={{ flexGrow: 1, width: "100%", minHeight: 200 }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={chartData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={80}
                  paddingAngle={5}
                  dataKey="value"
                >
                  <Cell key="cell-inflows" fill={COLORS.inflows} />
                  <Cell key="cell-outflows" fill={COLORS.outflows} />
                  <Cell key="cell-adjustments" fill={COLORS.adjustments} />
                </Pie>
                <Tooltip />
                <Legend
                  iconSize={10}
                  layout="vertical"
                  verticalAlign="middle"
                  align="right"
                />
              </PieChart>
            </ResponsiveContainer>
          </Box>
        ) : (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexGrow: 1,
            }}
          >
            <Typography
              variant="body2"
              sx={{ color: "text.secondary", fontStyle: "italic" }}
            >
              No inventory movements today.
            </Typography>
          </Box>
        )}
      </CardContent>
    </StyledCard>
  );
};

export default InventoryLogWidget;
